import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { alpha, useTheme, type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { SecureImage } from '@/components/secure-image';

export const CLASSIC_RELATIONSHIP_NODE_SIZE = {
  width: 132,
  height: 132,
};

export const RELATIONSHIP_MAP_SHAPE_THEME_STORAGE_KEY = 'novelweaver.relationshipMap.shapeTheme';

export const RELATIONSHIP_MAP_SHAPE_THEMES = [
  'classic',
  'cyberpunk',
  'fantasy',
  'horror',
  'romantasy',
] as const;

export type RelationshipMapShapeTheme = typeof RELATIONSHIP_MAP_SHAPE_THEMES[number];

type EntityShapeRole = 'characters' | 'organizations' | 'locations' | 'events' | 'codex';

type NodeDimensions = {
  width: number;
  height: number;
};

type RelationshipMapSurface = {
  background: string;
  gridColor: string;
  edgeColor: string;
  panelBackground: string;
  panelBorderColor: string;
  miniMapMaskColor: string;
};

type RelationshipGraphNodeShapeOwnProps = {
  shapeTheme: RelationshipMapShapeTheme;
  entityType: string;
  name: string;
  typeLabel: string;
  color: string;
  accentColor?: string;
  outlineColor?: string;
  imageSource: string;
  imagePath?: string;
  initials: string;
};

type RelationshipGraphNodeShapeProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  keyof RelationshipGraphNodeShapeOwnProps
> & RelationshipGraphNodeShapeOwnProps;

type ThemedNodeProps = RelationshipGraphNodeShapeOwnProps & {
  accent: string;
  theme: Theme;
};

const TECH_CARD_CLIP = 'polygon(9% 0, 100% 0, 100% 78%, 91% 100%, 0 100%, 0 22%)';
const PARCHMENT_CLIP = [
  'polygon(5% 4%, 18% 0, 31% 4%, 48% 0, 65% 5%, 83% 2%, 96% 7%,',
  '100% 22%, 96% 41%, 100% 61%, 94% 78%, 98% 95%, 76% 100%,',
  '55% 95%, 35% 100%, 13% 96%, 0 88%, 4% 67%, 0 48%, 5% 29%)',
].join(' ');
const SHIELD_CLIP = 'polygon(50% 0%, 92% 12%, 88% 67%, 50% 100%, 12% 67%, 8% 12%)';
const PLAQUE_CLIP = 'polygon(7% 0, 93% 0, 100% 18%, 100% 82%, 93% 100%, 7% 100%, 0 82%, 0 18%)';
const ROMANTASY_PLAQUE_CLIP = [
  'polygon(8% 0, 92% 0, 96% 7%, 100% 10%, 100% 90%, 96% 93%,',
  '92% 100%, 8% 100%, 4% 93%, 0 90%, 0 10%, 4% 7%)',
].join(' ');

const RELATIONSHIP_NODE_DIMENSIONS: Record<RelationshipMapShapeTheme, Record<EntityShapeRole | 'default', NodeDimensions>> = {
  classic: {
    characters: CLASSIC_RELATIONSHIP_NODE_SIZE,
    organizations: CLASSIC_RELATIONSHIP_NODE_SIZE,
    locations: CLASSIC_RELATIONSHIP_NODE_SIZE,
    events: CLASSIC_RELATIONSHIP_NODE_SIZE,
    codex: CLASSIC_RELATIONSHIP_NODE_SIZE,
    default: CLASSIC_RELATIONSHIP_NODE_SIZE,
  },
  cyberpunk: {
    characters: { width: 218, height: 96 },
    organizations: { width: 154, height: 168 },
    locations: { width: 206, height: 106 },
    events: { width: 190, height: 84 },
    codex: { width: 190, height: 84 },
    default: { width: 190, height: 84 },
  },
  fantasy: {
    characters: { width: 198, height: 140 },
    organizations: { width: 156, height: 176 },
    locations: { width: 180, height: 146 },
    events: { width: 188, height: 78 },
    codex: { width: 188, height: 78 },
    default: { width: 166, height: 150 },
  },
  horror: {
    characters: { width: 214, height: 118 },
    organizations: { width: 158, height: 178 },
    locations: { width: 172, height: 142 },
    events: { width: 190, height: 78 },
    codex: { width: 190, height: 78 },
    default: { width: 172, height: 142 },
  },
  romantasy: {
    characters: { width: 218, height: 118 },
    organizations: { width: 156, height: 178 },
    locations: { width: 180, height: 146 },
    events: { width: 190, height: 84 },
    codex: { width: 190, height: 84 },
    default: { width: 180, height: 146 },
  },
};

const RELATIONSHIP_THEME_ACCENTS: Record<RelationshipMapShapeTheme, Record<EntityShapeRole, string>> = {
  classic: {
    characters: '',
    organizations: '',
    locations: '',
    events: '',
    codex: '',
  },
  cyberpunk: {
    characters: '#28f6ff',
    organizations: '#f6a623',
    locations: '#61f279',
    events: '#ff5a53',
    codex: '#a66cff',
  },
  fantasy: {
    characters: '#4cc9ff',
    organizations: '#ffcf4a',
    locations: '#52e39a',
    events: '#ff6b7a',
    codex: '#b88cff',
  },
  horror: {
    characters: '#8a6f58',
    organizations: '#9a9174',
    locations: '#899767',
    events: '#a4312f',
    codex: '#7d4a82',
  },
  romantasy: {
    characters: '#c287ff',
    organizations: '#d6a13f',
    locations: '#a8c56f',
    events: '#e477a1',
    codex: '#9a7dff',
  },
};

export function isRelationshipMapShapeTheme(value: string | null): value is RelationshipMapShapeTheme {
  return RELATIONSHIP_MAP_SHAPE_THEMES.includes(value as RelationshipMapShapeTheme);
}

export function getRelationshipMapShapeThemeStorageKey(novelId?: string | null) {
  return novelId
    ? `${RELATIONSHIP_MAP_SHAPE_THEME_STORAGE_KEY}.${encodeURIComponent(novelId)}`
    : RELATIONSHIP_MAP_SHAPE_THEME_STORAGE_KEY;
}

function normalizeThemeSignal(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

export function getDefaultRelationshipMapShapeTheme(
  ...novelThemeSignals: Array<string | null | undefined>
): RelationshipMapShapeTheme {
  const normalizedTheme = normalizeThemeSignal(novelThemeSignals.filter(Boolean).join(' '));

  if (!normalizedTheme.trim()) return 'classic';
  if (/\b(cyberpunk|cyber|scifi|sci fi|science fiction|space opera)\b/.test(normalizedTheme)) {
    return 'cyberpunk';
  }
  if (/\b(romantasy|romance|romantic|fae|fairy)\b/.test(normalizedTheme)) {
    return 'romantasy';
  }
  if (/\b(horror|terror|dark|gothic|supernatural|occult)\b/.test(normalizedTheme)) {
    return 'horror';
  }
  if (/\b(fantasy|magic|magical|medieval|epic)\b/.test(normalizedTheme)) {
    return 'fantasy';
  }

  return 'classic';
}

export function getRelationshipEntityShapeRole(entityType?: string): EntityShapeRole {
  if (
    entityType === 'characters'
    || entityType === 'organizations'
    || entityType === 'locations'
    || entityType === 'events'
    || entityType === 'codex'
  ) {
    return entityType;
  }

  return 'codex';
}

export function getRelationshipNodeDimensions(
  entityType: string | undefined,
  shapeTheme: RelationshipMapShapeTheme,
): NodeDimensions {
  const role = getRelationshipEntityShapeRole(entityType);
  return RELATIONSHIP_NODE_DIMENSIONS[shapeTheme][role] || RELATIONSHIP_NODE_DIMENSIONS[shapeTheme].default;
}

export function getRelationshipNodeAccent(
  shapeTheme: RelationshipMapShapeTheme,
  entityType: string | undefined,
  fallbackColor: string,
) {
  const role = getRelationshipEntityShapeRole(entityType);
  return RELATIONSHIP_THEME_ACCENTS[shapeTheme][role] || fallbackColor;
}

export function getRelationshipMapSurface(
  shapeTheme: RelationshipMapShapeTheme,
  theme: Theme,
): RelationshipMapSurface {
  if (shapeTheme === 'cyberpunk') {
    return {
      background: [
        'radial-gradient(circle at 20% 20%, rgba(40, 246, 255, 0.13), transparent 28%)',
        'linear-gradient(135deg, #02070d 0%, #071420 52%, #02060b 100%)',
      ].join(', '),
      gridColor: alpha('#28f6ff', 0.18),
      edgeColor: alpha('#8bb8ff', 0.72),
      panelBackground: alpha('#06131c', 0.88),
      panelBorderColor: alpha('#28f6ff', 0.3),
      miniMapMaskColor: alpha('#02070d', 0.72),
    };
  }

  if (shapeTheme === 'fantasy') {
    return {
      background: [
        'radial-gradient(circle at 14% 18%, rgba(76, 201, 255, 0.18), transparent 28%)',
        'radial-gradient(circle at 84% 22%, rgba(184, 140, 255, 0.16), transparent 30%)',
        'radial-gradient(circle at 52% 88%, rgba(82, 227, 154, 0.13), transparent 32%)',
        'linear-gradient(135deg, #061522 0%, #10243a 52%, #07121f 100%)',
      ].join(', '),
      gridColor: alpha('#ffcf4a', 0.22),
      edgeColor: alpha('#9ddcff', 0.82),
      panelBackground: alpha('#091b2b', 0.9),
      panelBorderColor: alpha('#ffcf4a', 0.45),
      miniMapMaskColor: alpha('#06111d', 0.72),
    };
  }

  if (shapeTheme === 'horror') {
    return {
      background: [
        'radial-gradient(circle at 78% 18%, rgba(164, 49, 47, 0.12), transparent 24%)',
        'linear-gradient(135deg, #0a0a08 0%, #151411 55%, #080806 100%)',
      ].join(', '),
      gridColor: alpha('#8a6f58', 0.14),
      edgeColor: alpha('#9f3b37', 0.72),
      panelBackground: alpha('#17110f', 0.9),
      panelBorderColor: alpha('#a4312f', 0.35),
      miniMapMaskColor: alpha('#0a0a08', 0.72),
    };
  }

  if (shapeTheme === 'romantasy') {
    return {
      background: [
        'radial-gradient(circle at 24% 74%, rgba(194, 135, 255, 0.14), transparent 26%)',
        'linear-gradient(135deg, #100a19 0%, #151326 48%, #090915 100%)',
      ].join(', '),
      gridColor: alpha('#c287ff', 0.16),
      edgeColor: alpha('#cfa7ff', 0.72),
      panelBackground: alpha('#160f22', 0.9),
      panelBorderColor: alpha('#c287ff', 0.32),
      miniMapMaskColor: alpha('#0b0915', 0.72),
    };
  }

  return {
    background: theme.palette.background.default,
    gridColor: theme.palette.divider,
    edgeColor: theme.palette.text.secondary,
    panelBackground: alpha(theme.palette.background.paper, 0.92),
    panelBorderColor: theme.palette.divider,
    miniMapMaskColor: alpha(theme.palette.background.default, 0.75),
  };
}

function getFrameFill(shapeTheme: RelationshipMapShapeTheme, accent: string) {
  if (shapeTheme === 'cyberpunk') {
    return `linear-gradient(135deg, ${alpha(accent, 0.2)} 0%, rgba(4, 16, 24, 0.94) 60%)`;
  }

  if (shapeTheme === 'fantasy') {
    return `linear-gradient(145deg, ${alpha('#071827', 0.96)} 0%, ${alpha(accent, 0.38)} 100%)`;
  }

  if (shapeTheme === 'horror') {
    return `linear-gradient(145deg, ${alpha('#d7c5a5', 0.88)} 0%, ${alpha('#4c3228', 0.88)} 100%)`;
  }

  return `linear-gradient(145deg, ${alpha('#2f173d', 0.92)} 0%, ${alpha(accent, 0.24)} 100%)`;
}

function getTextColor(shapeTheme: RelationshipMapShapeTheme) {
  return shapeTheme === 'horror' ? '#1f1712' : '#fff7df';
}

function getSubtleTextColor(shapeTheme: RelationshipMapShapeTheme, accent: string) {
  return shapeTheme === 'horror' ? alpha('#1f1712', 0.76) : alpha(accent, 0.9);
}

function getFrameClip(shapeTheme: RelationshipMapShapeTheme, role: EntityShapeRole) {
  if (role === 'organizations') return SHIELD_CLIP;
  if (role === 'locations') return shapeTheme === 'cyberpunk' ? TECH_CARD_CLIP : PARCHMENT_CLIP;
  if (role === 'events' || role === 'codex') {
    return shapeTheme === 'romantasy' ? ROMANTASY_PLAQUE_CLIP : PLAQUE_CLIP;
  }
  return undefined;
}

function Media({
  imageSource,
  imagePath,
  name,
  fallback,
}: {
  imageSource: string;
  imagePath?: string;
  name: string;
  fallback: ReactNode;
}) {
  if (!imageSource) return fallback;

  return (
    <SecureImage
      src={imageSource}
      path={imagePath}
      alt={name}
      fallback={fallback}
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

function FrameShell({
  accent,
  borderRadius,
  children,
  clipPath,
  fill,
  glow,
  innerInset = 4,
}: {
  accent: string;
  borderRadius?: string | number;
  children: ReactNode;
  clipPath?: string;
  fill: string;
  glow: string;
  innerInset?: number;
}) {
  const shapeSx: SxProps<Theme> = clipPath ? { clipPath } : { borderRadius };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'visible' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: accent,
          filter: glow,
          ...shapeSx,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: innerInset,
          background: fill,
          overflow: 'hidden',
          ...shapeSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function ClassicNodeContent({
  accent,
  imagePath,
  imageSource,
  initials,
  name,
}: ThemedNodeProps) {
  const fallback = (
    <Avatar
      alt={name}
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: alpha(accent, 0.16),
        color: accent,
        fontSize: 34,
        fontWeight: 700,
      }}
    >
      {initials || '?'}
    </Avatar>
  );

  return (
    <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden' }}>
      <Media imageSource={imageSource} imagePath={imagePath} name={name} fallback={fallback} />
    </Box>
  );
}

function CharacterNodeContent(props: ThemedNodeProps) {
  const { accent, imagePath, imageSource, initials, name, shapeTheme, theme, typeLabel } = props;
  const textColor = getTextColor(shapeTheme);
  const fill = getFrameFill(shapeTheme, accent);
  const portraitSize = shapeTheme === 'cyberpunk' ? 70 : 76;
  const fallback = (
    <Avatar
      alt={name}
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: alpha(accent, 0.18),
        color: accent,
        fontWeight: 800,
      }}
    >
      {initials || '?'}
    </Avatar>
  );

  return (
    <FrameShell
      accent={accent}
      borderRadius="999px"
      fill={fill}
      glow={`drop-shadow(0 0 12px ${alpha(accent, shapeTheme === 'cyberpunk' ? 0.55 : shapeTheme === 'fantasy' ? 0.42 : 0.25)})`}
      innerInset={shapeTheme === 'cyberpunk' ? 3 : 4}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 10,
          top: '50%',
          width: portraitSize,
          height: portraitSize,
          borderRadius: '50%',
          overflow: 'hidden',
          transform: 'translateY(-50%)',
          border: `2px solid ${alpha(accent, 0.7)}`,
          bgcolor: alpha(theme.palette.common.black, 0.35),
        }}
      >
        <Media imageSource={imageSource} imagePath={imagePath} name={name} fallback={fallback} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: portraitSize + 22,
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          minWidth: 0,
        }}
      >
        <Typography
          noWrap
          sx={{
            color: shapeTheme === 'cyberpunk' ? accent : textColor,
            fontSize: 15,
            fontWeight: 850,
            letterSpacing: shapeTheme === 'cyberpunk' ? 0.8 : 0.2,
            lineHeight: 1.15,
            textTransform: shapeTheme === 'cyberpunk' ? 'uppercase' : 'none',
            textShadow: `0 0 10px ${alpha(accent, 0.45)}`,
          }}
        >
          {name}
        </Typography>
        <Typography
          noWrap
          sx={{
            mt: 0.25,
            color: getSubtleTextColor(shapeTheme, accent),
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {typeLabel}
        </Typography>
      </Box>
    </FrameShell>
  );
}

function ShieldNodeContent(props: ThemedNodeProps) {
  const { accent, imagePath, imageSource, initials, name, outlineColor, shapeTheme, theme, typeLabel } = props;
  const outline = outlineColor || accent;
  const fill = getFrameFill(shapeTheme, accent);
  const fallback = (
    <Avatar
      alt={name}
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: alpha(accent, 0.16),
        color: accent,
        borderRadius: 0,
        fontSize: 34,
        fontWeight: 900,
      }}
    >
      {initials || '?'}
    </Avatar>
  );

  return (
    <FrameShell
      accent={outline}
      clipPath={SHIELD_CLIP}
      fill={fill}
      glow={`drop-shadow(0 8px 14px ${alpha(outline, 0.36)})`}
      innerInset={5}
    >
      <Box sx={{ position: 'absolute', inset: 0, opacity: imageSource ? 0.22 : 0.08 }}>
        <Media imageSource={imageSource} imagePath={imagePath} name={name} fallback={fallback} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: '18px 18px 30px',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          noWrap
          sx={{
            width: '100%',
            color: shapeTheme === 'cyberpunk' ? accent : '#ffffff',
            fontSize: 15,
            fontWeight: 900,
            letterSpacing: 0.45,
            textShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.62)}`,
            textTransform: shapeTheme === 'cyberpunk' ? 'uppercase' : 'none',
          }}
        >
          {name}
        </Typography>
        <Typography
          noWrap
          sx={{
            width: '100%',
            mt: 0.5,
            color: shapeTheme === 'cyberpunk' ? getSubtleTextColor(shapeTheme, accent) : alpha('#ffffff', 0.88),
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {typeLabel}
        </Typography>
      </Box>
    </FrameShell>
  );
}

function CardNodeContent(props: ThemedNodeProps) {
  const { accent, entityType, imagePath, imageSource, initials, name, shapeTheme, theme, typeLabel } = props;
  const role = getRelationshipEntityShapeRole(entityType);
  const clipPath = getFrameClip(shapeTheme, role);
  const isEvent = role === 'events' || role === 'codex';
  const textColor = getTextColor(shapeTheme);
  const fallback = (
    <Avatar
      alt={name}
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: alpha(accent, 0.16),
        color: accent,
        borderRadius: 0,
        fontWeight: 900,
      }}
    >
      {initials || '?'}
    </Avatar>
  );

  return (
    <FrameShell
      accent={accent}
      clipPath={clipPath}
      fill={getFrameFill(shapeTheme, accent)}
      glow={shapeTheme === 'fantasy'
        ? `drop-shadow(0 6px 12px ${alpha(accent, 0.34)})`
        : `drop-shadow(0 6px 10px ${alpha(theme.palette.common.black, 0.34)})`}
      innerInset={shapeTheme === 'cyberpunk' ? 3 : 5}
    >
      <Box sx={{ position: 'absolute', inset: 0, opacity: imageSource ? (isEvent ? 0.18 : 0.72) : 0.12 }}>
        <Media imageSource={imageSource} imagePath={imagePath} name={name} fallback={fallback} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: isEvent ? '12px 16px' : 'auto 12px 12px',
          display: 'grid',
          alignContent: isEvent ? 'center' : 'end',
          justifyItems: 'center',
          minWidth: 0,
          textAlign: 'center',
        }}
      >
        <Typography
          noWrap
          sx={{
            width: '100%',
            color: shapeTheme === 'cyberpunk' ? accent : textColor,
            fontSize: isEvent ? 14 : 13,
            fontWeight: 850,
            letterSpacing: shapeTheme === 'cyberpunk' ? 0.65 : 0.1,
            lineHeight: 1.15,
            textTransform: shapeTheme === 'cyberpunk' ? 'uppercase' : 'none',
            textShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.55)}`,
          }}
        >
          {name}
        </Typography>
        <Typography
          noWrap
          sx={{
            width: '100%',
            mt: 0.35,
            color: getSubtleTextColor(shapeTheme, accent),
            fontSize: 10,
            fontWeight: 750,
          }}
        >
          {typeLabel}
        </Typography>
      </Box>
    </FrameShell>
  );
}

export const RelationshipGraphNodeShape = forwardRef<HTMLDivElement, RelationshipGraphNodeShapeProps>(
  function RelationshipGraphNodeShape({
    accentColor,
    color,
    entityType,
    imagePath,
    imageSource,
    initials,
    name,
    outlineColor,
    shapeTheme,
    typeLabel,
    ...containerProps
  }, ref) {
  const theme = useTheme();
  const role = getRelationshipEntityShapeRole(entityType);
  const accent = accentColor || getRelationshipNodeAccent(shapeTheme, entityType, color);
  const shapeProps = {
    color,
    entityType,
    imagePath,
    imageSource,
    initials,
    name,
    outlineColor,
    shapeTheme,
    typeLabel,
  };
  const themedProps = { ...shapeProps, accent, theme };
  let content: ReactNode;

  if (shapeTheme === 'classic') {
    content = <ClassicNodeContent {...themedProps} />;
  } else if (role === 'characters') {
    content = <CharacterNodeContent {...themedProps} />;
  } else if (role === 'organizations') {
    content = <ShieldNodeContent {...themedProps} />;
  } else {
    content = <CardNodeContent {...themedProps} />;
  }

  return (
    <Box
      {...containerProps}
      ref={ref}
      sx={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {content}
    </Box>
  );
  },
);
